import { createFileRoute } from '@tanstack/react-router';
import { Prose } from '@leanin/ui/server';
import { LegalShell } from '@/components/legal/LegalShell';

export const Route = createFileRoute('/community-guidelines')({
  component: CommunityGuidelinesPage,
});

function CommunityGuidelinesPage() {
  return (
    <LegalShell title="Community Guidelines">
      <Prose>
        <h2>Welcome to Lean In Connect</h2>
        <p>
          This community exists to help people connect, learn, support one
          another, and grow together. Meaningful conversations happen when
          people feel respected, included, and safe enough to share honestly.
        </p>
        <p>
          Every member helps shape this space. The way we show up matters.
        </p>
        <p>
          Lean In provides all of its programs, resources, Lean In Circles,
          and Lean In Connect free of charge because we never want cost to be
          a barrier to our community.{' '}
          <strong>
            Members may not charge for participation, sell products or
            services, recruit clients, or use this community for personal
            financial gain.
          </strong>{' '}
          Lean In Connect and Lean In Circles exist to support learning,
          connection, and shared growth.
        </p>
        <p>
          We want people of different backgrounds, identities, experiences,
          and perspectives to feel welcome here. These guidelines are
          designed to help create a community rooted in curiosity, respect,
          and care — where everyone can participate with confidence.
        </p>

        <h2>How we show up together</h2>

        <h3>Lead with respect</h3>
        <p>
          Treat others with dignity, even when you disagree. Healthy
          conversations include different perspectives.
        </p>
        <p>
          Assume good intent, and stay open to feedback when someone tells
          you your words or actions caused harm.
        </p>

        <h3>Contribute thoughtfully</h3>
        <p>
          Share experiences, ideas, questions, and resources that help move
          conversations forward. Strong communities are built by people who
          participate generously and thoughtfully.
        </p>
        <p>Before posting, ask yourself:</p>
        <ul>
          <li>Is this constructive?</li>
          <li>Is this relevant?</li>
          <li>
            Is this helping create the kind of community I want to be part
            of?
          </li>
        </ul>
        <p>
          Community conversations are intended for peer support, shared
          learning, and connection. Content shared in this space should not
          be considered professional advice, be it legal, medical, mental
          health, financial, career, or other specialized advice.
        </p>

        <h3>Support one another</h3>
        <p>
          Encouragement matters. A thoughtful response, a helpful resource,
          or simply acknowledging someone&rsquo;s experience can make a real
          difference.
        </p>
        <p>
          We understand that no one gets everything right all the time. We
          do expect empathy, accountability, and a willingness to learn.
        </p>

        <h3>Make space for different perspectives</h3>
        <p>
          Healthy communities make room for many voices and experiences.
          Some people naturally contribute more quickly or more frequently
          in group discussions, while others may need more encouragement to
          participate.
        </p>
        <p>
          Be mindful of how much space you take up in conversations, and
          help create opportunities for others to contribute.
        </p>

        <h3>Respect privacy and trust</h3>
        <p>
          What people share here may be personal. Do not repost, screenshot,
          or share someone else&rsquo;s content or private information
          without their consent.
        </p>

        <h2>Behavior that is not allowed</h2>
        <p>
          To help protect the community, the following behaviors are not
          permitted:
        </p>
        <ul>
          <li>Demeaning, hostile, or intentionally exclusionary behavior</li>
          <li>Hate speech or discrimination</li>
          <li>Harassment, bullying, or threats</li>
          <li>Trolling, baiting, or intentionally disruptive behavior</li>
          <li>Personal attacks or shaming</li>
          <li>Sexual harassment or unwanted advances</li>
          <li>Sharing private or confidential information</li>
          <li>Spam, scams, or misleading content</li>
          <li>
            Unsolicited promotion, recruiting, sales outreach, or repeated
            requests for members to purchase products, services, or programs
            outside the intended purpose of the community
          </li>
          <li>Pornographic, sexually explicit, or mature content</li>
          <li>Illegal, dangerous, or harmful content</li>
        </ul>
        <p>
          Content or behavior that undermines the safety, trust, or
          integrity of the community may result in moderation action.
        </p>

        <h2>How moderation works</h2>
        <p>
          Our moderation team is here to support healthy participation and
          uphold these guidelines fairly and consistently.
        </p>
        <p>Depending on the situation, moderators may:</p>
        <ul>
          <li>Remove content</li>
          <li>Issue warnings</li>
          <li>Limit account access</li>
          <li>Suspend or remove members from the platform</li>
        </ul>
        <p>
          We recognize that context matters. Moderation decisions are made
          thoughtfully, with the goal of protecting the community as a
          whole.
        </p>
        <p>
          If you believe a moderation action was made in error, you can
          reach out to{' '}
          <a href="mailto:info@leanin.org">info@leanin.org</a> to request a
          review.
        </p>

        <h2>Reporting your concerns</h2>
        <p>
          If you experience or witness behavior that violates these
          guidelines, please use the platform&rsquo;s report feature so our
          team can review it.
        </p>
        <p>
          Speaking up helps create a stronger, safer community for everyone.
          We review reports carefully and aim to respond promptly and
          respectfully.
        </p>

        <h2>A shared responsibility</h2>
        <p>
          Supportive and inclusive communities do not happen by accident.
          They are built by people who choose to contribute with care,
          accountability, and respect.
        </p>
        <p>
          Thank you for being part of this community and helping create a
          space where people can connect, contribute, and belong.
        </p>
      </Prose>
    </LegalShell>
  );
}
